import { formatCentsToUsd } from '../../../helpers/formatCentsToUsd';
import {
    centsToXlsxNumber,
    applyCurrencyFormat,
    writeWorkbookAndSave,
    buildDateRangeLabel,
    buildFilenameRange,
} from '../../../helpers/excelExportHelpers';
import { aggregateBreakdown } from './aggregateBreakdown';

// English label for a group row in the export (the sheet is English-only).
const affiliateLabel = (item) => {
    if (item.commissionGroupId !== null) return item.affiliateName || '';
    switch (item.noAffiliateType) {
        case 'online':
            return 'Online';
        case 'agents':
            return 'Agents (no commission group)';
        case 'agent':
            return item.affiliateName || 'No affiliate';
        default:
            return 'No affiliate';
    }
};

export const exportAffiliateSalesReportToXlsx = async (
    reportData,
    dateFrom,
    dateTo,
    { showTicketType = true, showTicketOption = true } = {},
) => {
    const XLSX = await import('xlsx');

    const showBreakdown = showTicketType || showTicketOption;

    // Include the ticket-type / ticket-option columns only when their toggle is
    // on, matching the on-screen table.
    const ticketCols = (typeVal, optionVal) => [
        ...(showTicketType ? [typeVal] : []),
        ...(showTicketOption ? [optionVal] : []),
    ];

    const sheetData = [];

    const rangeLabel = buildDateRangeLabel(dateFrom, dateTo);

    sheetData.push(['Affiliate Sales Report']);
    sheetData.push([`Date Range: ${rangeLabel}`]);
    sheetData.push([
        `Total Sales: ${formatCentsToUsd(reportData.totalSales || 0)}`,
        `Total Bookings: ${reportData.totalBookings || 0}`,
        `Total Pax: ${reportData.totalPax || 0}`,
    ]);
    sheetData.push([]);

    sheetData.push([
        'Affiliate',
        ...ticketCols('Ticket Type', 'Ticket Option'),
        'Bookings',
        'Pax',
        'Total Sales',
        '% of Total',
    ]);

    (reportData.items || []).forEach((item) => {
        sheetData.push([
            affiliateLabel(item),
            ...ticketCols('', ''),
            item.bookingCount,
            item.totalPax,
            centsToXlsxNumber(item.totalSales),
            typeof item.percentageOfTotal === 'number' ? item.percentageOfTotal : '',
        ]);

        if (showBreakdown) {
            aggregateBreakdown(item.breakdown || [], showTicketType, showTicketOption).forEach(
                (bd) => {
                    sheetData.push([
                        '',
                        ...ticketCols(bd.ticketTypeName || '-', bd.ticketOptionName || '-'),
                        '',
                        bd.itemCount,
                        centsToXlsxNumber(bd.totalSales),
                        '',
                    ]);
                },
            );
        }
    });

    const totalSales = reportData.totalSales || 0;
    sheetData.push([
        'Grand Total',
        ...ticketCols('', ''),
        reportData.totalBookings || 0,
        reportData.totalPax || 0,
        centsToXlsxNumber(totalSales),
        totalSales === 0 ? 0 : 100,
    ]);

    const ws = XLSX.utils.aoa_to_sheet(sheetData);
    ws['!cols'] = [
        { wch: 30 },
        ...ticketCols({ wch: 25 }, { wch: 20 }),
        { wch: 12 },
        { wch: 10 },
        { wch: 15 },
        { wch: 12 },
    ];
    // Total Sales / % of Total sit right after the (variable) ticket columns.
    const salesCol = 3 + (showTicketType ? 1 : 0) + (showTicketOption ? 1 : 0);
    applyCurrencyFormat(XLSX, ws, sheetData, [salesCol]);
    applyCurrencyFormat(XLSX, ws, sheetData, [salesCol + 1], '0.00"%"');

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Affiliate Sales Report');

    const filenameRange = buildFilenameRange(dateFrom, dateTo);
    writeWorkbookAndSave(XLSX, wb, `affiliate-sales-report-${filenameRange}.xlsx`);
};

export default exportAffiliateSalesReportToXlsx;
